// SPVolatilityMatrix.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DataGrid, { Column } from "devextreme-react/data-grid";
import { DateBox } from "devextreme-react/date-box";
import { SelectBox } from "devextreme-react/select-box";
import { Button } from "devextreme-react/button";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

import "./SPMatrixGrid.css";

import { toDate, fmt } from "../../utils/dateUtils";
import { fmtNum, fmtVolMove } from "../../utils/formatUtils";
import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";

interface ColDef {
  key: string;
  caption: string;
}

interface MatrixRow {
  vol_move: number | string | null;
  m30: number | null;
  m20: number | null;
  m10: number | null;
  p00: number | null;
  p10: number | null;
  p20: number | null;
  p30: number | null;
}

interface MeasureOption {
  id: string;
  value: string;
}

interface MatrixGridProps {
  title?: string;
  data: MatrixRow[];
}

const COLS: ColDef[] = [
  { key: "vol_move", caption: "Vol move" },
  { key: "m30", caption: "-30.00%" },
  { key: "m20", caption: "-20.00%" },
  { key: "m10", caption: "-10.00%" },
  { key: "p00", caption: "0.00%" },
  { key: "p10", caption: "10.00%" },
  { key: "p20", caption: "20.00%" },
  { key: "p30", caption: "30.00%" },
];

function buildExportRows(data: MatrixRow[]): string[][] {
  const rows: string[][] = [];
  rows.push(COLS.map((c) => c.caption));

  (Array.isArray(data) ? data : []).forEach((r) => {
    rows.push([
      fmtVolMove(r.vol_move),
      fmtNum(r.m30),
      fmtNum(r.m20),
      fmtNum(r.m10),
      fmtNum(r.p00),
      fmtNum(r.p10),
      fmtNum(r.p20),
      fmtNum(r.p30),
    ]);
  });

  return rows;
}

// API returns keys like "−30" and "10" etc. Normalize to stable fields
const normalizeRow = (r: any): MatrixRow => ({
  vol_move: r.vol_move,
  m30: r["−30"] ?? r["-30"] ?? null,
  m20: r["−20"] ?? r["-20"] ?? null,
  m10: r["−10"] ?? r["-10"] ?? null,
  p00: r["0"] ?? r[0] ?? null,
  p10: r["10"] ?? r[10] ?? null,
  p20: r["20"] ?? r[20] ?? null,
  p30: r["30"] ?? r[30] ?? null,
});

const normalizeMatrix = (arr: any): MatrixRow[] =>
  Array.isArray(arr) ? arr.map(normalizeRow) : [];

const SPMatrixGrid: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const [cobDate, setCobDate] = useState<Date | null>(null);
  const [, , , latestCobDate] = useCobDate("/market_risk/sp_matrix_latest_cob");
  const [compareDate, setCompareDate] = useState<Date | null>(null);

  const [measureId, setMeasureId] = useState<string>("PnL");
  const [measureName, setMeasureName] = useState<string>("");

  const [matrix, setMatrix] = useState<MatrixRow[]>([]);
  const [comparisonMatrix, setComparisonMatrix] = useState<MatrixRow[]>([]);

  const MEASURES = useMemo<MeasureOption[]>(
    () => [
      { id: "P&L impact (€)", value: "PnL" },
      { id: "Theta (€ per day)", value: "Theta" },
      { id: "Delta (lots)", value: "Delta" },
      { id: "Vega (€ per 1%)", value: "Vega" },
    ],
    []
  );

  const cobStr = useMemo(() => fmt(cobDate), [cobDate?.getTime()]);
  const cmpStr = useMemo(() => fmt(compareDate), [compareDate?.getTime()]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const qs = new URLSearchParams();
        if (cobStr) qs.set("cob_date", cobStr);
        if (cmpStr) qs.set("comparison_cob_date", cmpStr);
        if (measureId) qs.set("measure", measureId);

        const url = `${baseUrl}/market_risk/sp_matrix${qs.toString() ? `?${qs}` : ""}`;
        const r = await fetch(url, { signal: ac.signal });

        if (!r.ok) {
          let body = "";
          try {
            body = await r.text();
          } catch (e) {
            body = "";
          }
          console.error("sp HTTP error", r.status, body);
          return;
        }

        const j = await r.json();

        if (j?.cob_date) setCobDate(toDate(j.cob_date));
        if (j?.comparison_cob_date) setCompareDate(toDate(j.comparison_cob_date));
        if (j?.measure) setMeasureName(j.measure);

        setMatrix(normalizeMatrix(j?.matrix));
        setComparisonMatrix(normalizeMatrix(j?.comparison_matrix));
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error("sp fetch failed", e);
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [baseUrl, cobStr, cmpStr, measureId]);

  const onCellPrepared = (e: any): void => {
    if (e.rowType === "header") {
      e.cellElement.classList.add("sp-matrix__header-cell");
      return;
    }

    if (e.rowType !== "data") return;

    if (e.column?.dataField === "vol_move") {
      e.cellElement.classList.add("sp-matrix__label-cell");
      return;
    }

    const v = Number(e.value);
    if (!Number.isFinite(v)) return;

    if (v < 0) e.cellElement.classList.add("sp-matrix__neg");
  };

  const exportExcel = async (): Promise<void> => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("SP Matrix");

    const headerFill: any = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F4E79" },
    };
    const headerFont: any = { bold: true, color: { argb: "FFFFFFFF" } };

    const setColWidths = (): void => {
      ws.getColumn(1).width = 14;
      for (let c = 2; c <= 8; c++) ws.getColumn(c).width = 14;
    };

    const styleHeaderRow = (rowNumber: number): void => {
      const row = ws.getRow(rowNumber);
      row.height = 18;
      for (let c = 1; c <= 8; c++) {
        const cell = row.getCell(c);
        cell.fill = headerFill;
        cell.font = headerFont;
        cell.alignment = {
          vertical: "middle",
          horizontal: c === 1 ? "left" : "right",
        };
      }
    };

    const styleBody = (startRow: number, endRow: number): void => {
      for (let r = startRow; r <= endRow; r++) {
        const row = ws.getRow(r);
        row.height = 16;

        const volCell = row.getCell(1);
        volCell.fill = headerFill;
        volCell.font = headerFont;
        volCell.alignment = { vertical: "middle", horizontal: "left" };

        for (let c = 2; c <= 8; c++) {
          const cell = row.getCell(c);
          const v = cell.value == null ? "" : String(cell.value);
          const isNegParen = v.includes("(") && v.includes(")");
          cell.font = isNegParen ? { color: { argb: "FFFF0000" } } : {};
          cell.alignment = { vertical: "middle", horizontal: "right" };
        }
      }
    };

    const addTable = (
      dateLabel: string,
      dateValue: string | null,
      data: MatrixRow[],
      startRow: number
    ): number => {
      ws.getRow(startRow).getCell(1).value = `${dateLabel}:`;
      ws.getRow(startRow).getCell(2).value = dateValue || "";
      ws.getRow(startRow).getCell(1).font = { bold: true };

      const tableHeaderRow = startRow + 1;
      const rows = buildExportRows(data);

      ws.getRow(tableHeaderRow).values = [rows[0][0], ...rows[0].slice(1)];
      styleHeaderRow(tableHeaderRow);

      let rPtr = tableHeaderRow + 1;
      for (let i = 1; i < rows.length; i++) {
        ws.getRow(rPtr).values = [rows[i][0], ...rows[i].slice(1)];
        rPtr += 1;
      }

      const lastBodyRow = rPtr - 1;
      styleBody(tableHeaderRow + 1, lastBodyRow);

      return lastBodyRow + 2;
    };

    setColWidths();

    let rowPtr = 1;

    ws.getRow(rowPtr).getCell(1).value = "Structured Products Matrix";
    ws.getRow(rowPtr).getCell(1).font = { bold: true, size: 14 };
    rowPtr += 2;

    ws.getRow(rowPtr).getCell(1).value = "Measure:";
    ws.getRow(rowPtr).getCell(2).value = measureName || measureId || "";
    ws.getRow(rowPtr).getCell(1).font = { bold: true };
    rowPtr += 1;

    rowPtr = addTable("CoB date", cobStr, matrix, rowPtr);
    rowPtr += 1;
    rowPtr = addTable("Comparison date", cmpStr, comparisonMatrix, rowPtr);

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileDate = cobStr || cmpStr || fmt(new Date()) || "export";
    saveAs(blob, `SP_Matrix_${fileDate}.xlsx`);
  };

  const exportPdf = (): void => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(14);
    doc.text("Structured Products Matrix", 40, 40);

    doc.setFontSize(10);
    doc.text(`Measure: ${measureName || measureId || ""}`, 40, 60);

    const colWidths = [70, 90, 90, 90, 90, 90, 90, 90];

    const makeTable = (dateLine: string, data: MatrixRow[], startY: number): number => {
      doc.setFontSize(10);
      doc.text(dateLine, 40, startY);

      const head = [COLS.map((c) => c.caption)];
      const body = (Array.isArray(data) ? data : []).map((r) => [
        fmtVolMove(r.vol_move),
        fmtNum(r.m30),
        fmtNum(r.m20),
        fmtNum(r.m10),
        fmtNum(r.p00),
        fmtNum(r.p10),
        fmtNum(r.p20),
        fmtNum(r.p30),
      ]);

      (doc as any).autoTable({
        startY: startY + 10,
        head,
        body,
        margin: { left: 40, right: 40 },
        styles: {
          fontSize: 9,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fontStyle: "bold",
          valign: "middle",
        },
        tableWidth: "auto",
        columnStyles: {
          0: { cellWidth: colWidths[0], halign: "center" },
          1: { cellWidth: colWidths[1], halign: "right" },
          2: { cellWidth: colWidths[2], halign: "right" },
          3: { cellWidth: colWidths[3], halign: "right" },
          4: { cellWidth: colWidths[4], halign: "right" },
          5: { cellWidth: colWidths[5], halign: "right" },
          6: { cellWidth: colWidths[6], halign: "right" },
          7: { cellWidth: colWidths[7], halign: "right" },
        },
        didParseCell: (cellData: any) => {
          if (cellData.section === "body") {
            const txt = (cellData.cell.text || []).join("");
            if (txt.includes("(") && txt.includes(")")) {
              cellData.cell.styles.textColor = [255, 0, 0];
            }
          }
        },
      });

      return (doc as any).lastAutoTable.finalY + 20;
    };

    let y = 90;
    y = makeTable(`CoB date: ${cobStr || ""}`, matrix, y);
    y = makeTable(`Comparison date: ${cmpStr || ""}`, comparisonMatrix, y);

    const fileDate = cobStr || cmpStr || fmt(new Date()) || "export";
    doc.save(`SP_Matrix_${fileDate}.pdf`);
  };

  const MatrixGrid: React.FC<MatrixGridProps> = ({ title, data }) => (
    <div className="sp-matrix__section">
      <div className="sp-matrix__title">{title}</div>

      <DataGrid
        className="sp-matrix__grid"
        dataSource={data}
        showBorders
        columnAutoWidth={false}
        hoverStateEnabled={false}
        rowAlternationEnabled={false}
        onCellPrepared={onCellPrepared}
        wordWrapEnabled={false}
        showColumnLines
        showRowLines
        columnFixing={{ enabled: true }}
      >
        <Column
          dataField="vol_move"
          caption="Vol move"
          alignment="center"
          width={90}
          fixed
          fixedPosition="left"
          cssClass="sp-matrix__label-col"
          allowSorting={false}
          allowFiltering={false}
          allowEditing={false}
          customizeText={(cell: any) => fmtVolMove(cell.value)}
        />

        <Column
          caption="Parallel price move"
          alignment="center"
          cssClass="sp-matrix__band"
        >
          <Column
            dataField="m30"
            caption="-30.00%"
            alignment="right"
            width={120}
            customizeText={(c: any) => fmtNum(c.value)}
          />
          <Column
            dataField="m20"
            caption="-20.00%"
            alignment="right"
            width={120}
            customizeText={(c: any) => fmtNum(c.value)}
          />
          <Column
            dataField="m10"
            caption="-10.00%"
            alignment="right"
            width={120}
            customizeText={(c: any) => fmtNum(c.value)}
          />
          <Column
            dataField="p00"
            caption="0.00%"
            alignment="right"
            width={120}
            customizeText={(c: any) => fmtNum(c.value)}
          />
          <Column
            dataField="p10"
            caption="10.00%"
            alignment="right"
            width={120}
            customizeText={(c: any) => fmtNum(c.value)}
          />
          <Column
            dataField="p20"
            caption="20.00%"
            alignment="right"
            width={120}
            customizeText={(c: any) => fmtNum(c.value)}
          />
          <Column
            dataField="p30"
            caption="30.00%"
            alignment="right"
            width={140}
            customizeText={(c: any) => fmtNum(c.value)}
          />
        </Column>
      </DataGrid>
    </div>
  );

  return (
    <div className="sp-matrix">
      {/* Compact top filters: CoB + Measure */}
      <div className="sp-matrix__filters sp-matrix__filters--compact">
        <div className="sp-matrix__filter sp-matrix__filter--compact">
          <label className="sp-matrix__label">CoB date</label>
          <DateBox
            className="sp-matrix__control"
            displayFormat="dd/MM/yyyy"
            useMaskBehavior
            pickerType="calendar"
            value={cobDate}
            onValueChanged={(e: any) => setCobDate(toDate(e.value))}
            width={140}
            height={28}
            max={latestCobDate ?? undefined}
          />
        </div>

        <div className="sp-matrix__filter sp-matrix__filter--compact">
          <label className="sp-matrix__label">Measure</label>
          <SelectBox
            className="sp-matrix__control"
            dataSource={MEASURES}
            valueExpr="value"
            displayExpr="id"
            value={measureId}
            width={210}
            height={28}
            onValueChanged={(e: any) => setMeasureId(e.value)}
          />
        </div>
      </div>
      <div
        className="sp-matrix__filter sp-matrix__filter--compact"
        style={{ marginLeft: "auto" }}
      >
        <div className="sp-matrix__export">
          <Button
            style={{
              padding: "10px 18px",
              borderRadius: "4px",
              backgroundColor: "#d9534f",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
            text="Export Excel"
            stylingMode="contained"
            type="default"
            onClick={exportExcel}
            disabled={loading}
            height={30}
          />
          <Button
            style={{
              padding: "10px 18px",
              borderRadius: "4px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
            text="Export PDF"
            stylingMode="contained"
            type="normal"
            onClick={exportPdf}
            disabled={loading}
            height={30}
          />
        </div>
      </div>

      {/* First grid */}
      <MatrixGrid data={matrix} />

      {/* Comparison control AFTER first grid */}
      <div className="sp-matrix__filters sp-matrix__filters--compact sp-matrix__filters--after-grid">
        <div className="sp-matrix__filter sp-matrix__filter--compact">
          <label className="sp-matrix__label">Comparison date</label>
          <DateBox
            className="sp-matrix__control"
            displayFormat="dd/MM/yyyy"
            useMaskBehavior
            pickerType="calendar"
            value={compareDate}
            onValueChanged={(e: any) => setCompareDate(toDate(e.value))}
            width={140}
            height={28}
            max={latestCobDate ?? undefined}
          />
        </div>
      </div>

      {/* Second grid */}
      <MatrixGrid data={comparisonMatrix} />
    </div>
  );
};

export default SPMatrixGrid;
